import MemberService from './MemberService';
import axios from 'axios';

// Cache for storing URLs to prevent flicker
const resourceCache = {
  backgroundImage: null,
  portalLogo: null,
  favicon: null,
  logoIcon: null,
  dividerColor: null,
  panelColor: null
};

/**
 * Utility functions to update dynamic resources in the application
 */
const DynamicResourcesUtils = {
  /**
   * Retry a function on network errors
   * @param {Function} fn - The function to retry
   * @param {number} maxRetries - Maximum number of retries
   * @returns {Promise} - Result of the function
   */
  retryWithAuth: async (fn, maxRetries = 2) => {
    let retries = 0;
    
    const attempt = async () => {
      try {
        return await fn();
      } catch (error) {
        if (axios.isCancel(error)) {
          throw error; // Don't retry cancelled requests
        }
        
        // The HttpService interceptor handles 401s automatically.
        // We should only retry on transient network or server errors.
        if ((!error.response || error.response.status >= 500) && retries < maxRetries) {
          retries++;
          console.log(`Network error, retrying (${retries}/${maxRetries})...`);
          
          // Wait a bit before retrying to avoid hammering the server
          await new Promise(resolve => setTimeout(resolve, 500 + retries * 500));
          return attempt();
        }
        
        // If it's not a retriable error (like a 404) or max retries are exceeded, throw.
        throw error;
      }
    };
    
    return attempt();
  },

  /**
   * Updates the favicon in the DOM using the base64 data from the API
   * @param {Object} cancelToken - Optional axios cancel token
   * @returns {Promise} - Promise that resolves when the favicon is updated
   */
  updateFavicon: (cancelToken) => {
    return DynamicResourcesUtils.retryWithAuth(async () => {
      const faviconBase64 = await MemberService.getFavicon(cancelToken);
      if (window.updateFaviconInDOM && faviconBase64) {
        window.updateFaviconInDOM(faviconBase64);
      }
      return faviconBase64;
    }).catch(error => {
      if (!axios.isCancel(error)) {
        console.error('Failed to update favicon:', error);
      }
      return null;
    });
  },

  /**
   * Updates the background image CSS in the DOM using the data from the API
   * @param {Object} cancelToken - Optional axios cancel token
   * @returns {Promise} - Promise that resolves when the background image is updated
   */  
  updateBackgroundImage: (cancelToken) => {
    return DynamicResourcesUtils.retryWithAuth(async () => {
      const data = await MemberService.getBackgroundImage(cancelToken);
      
      if (data && typeof data === 'string') {
        // Extract image src if data contains an img tag
        let backgroundImageUrl = data;
        
        // Check if data is an HTML img tag
        if (data.includes('<img') && data.includes('src=')) {
          const srcMatch = data.match(/src=["']([^"']*)["']/i);
          if (srcMatch && srcMatch[1]) {
            backgroundImageUrl = `url("${srcMatch[1]}")`;
          }
        }
        
        // Store in cache for persistence
        resourceCache.backgroundImage = backgroundImageUrl;
        
        // Find existing style element or create a new one
        let styleEl = document.getElementById('dynamic-background-image-style');
        if (!styleEl) {
          styleEl = document.createElement('style');
          styleEl.id = 'dynamic-background-image-style';
          document.head.appendChild(styleEl);
        }
        
        // Apply the style to the specific selectors that were using background-image-fallback.jpg
        styleEl.textContent = `
          /* Set CSS variable for the background image */
          :root {
            --dynamic-background-image: ${backgroundImageUrl};
          }
          
          /* Direct override for the selectors */
          .left-panel,
          .logo-panel {
            background: ${backgroundImageUrl} !important;
            background-size: auto 100% !important;
          }
          
          /* Ensure the background doesn't repeat in mobile view */
          @media (max-width: 899.98px) {
            .left-panel,
            .logo-panel {
              background-size: cover !important;
              background-repeat: no-repeat !important;
              background-position: center center !important;
            }
          }
        `;
        
        console.log('Background image updated successfully');
        return true;
      } else if (resourceCache.backgroundImage) {
        // If we have a cached value but the API call returned nothing valid, use the cached value
        console.log('Using cached background image');
        
        // Find existing style element or create a new one
        let styleEl = document.getElementById('dynamic-background-image-style');
        if (!styleEl) {
          styleEl = document.createElement('style');
          styleEl.id = 'dynamic-background-image-style';
          document.head.appendChild(styleEl);
        }
        
        styleEl.textContent = `
          /* Set CSS variable for the background image from cache */
          :root {
            --dynamic-background-image: ${resourceCache.backgroundImage};
          }
          
          /* Direct override for the selectors from cache */
          .left-panel,
          .logo-panel {
            background: ${resourceCache.backgroundImage} !important;
            background-size: auto 100% !important;
          }
          
          /* Ensure the background doesn't repeat in mobile view */
          @media (max-width: 899.98px) {
            .left-panel,
            .logo-panel {
              background-size: cover !important;
              background-repeat: no-repeat !important;
              background-position: center center !important;
            }
          }
        `;
        
        return true;
      } else {
        console.warn('Invalid background image data received');
        return false;
      }
    }).catch(error => {
      if (!axios.isCancel(error)) {
        console.error('Error updating background image:', error);
        
        // If we have a cached image, use it as fallback on error
        if (resourceCache.backgroundImage) {
          console.log('Using cached background image after error');
          
          // Find existing style element or create a new one
          let styleEl = document.getElementById('dynamic-background-image-style');
          if (!styleEl) {
            styleEl = document.createElement('style');
            styleEl.id = 'dynamic-background-image-style';
            document.head.appendChild(styleEl);
          }
          
          styleEl.textContent = `
            /* Set CSS variable for the background image from cache */
            :root {
              --dynamic-background-image: ${resourceCache.backgroundImage};
            }
            
            /* Direct override for the selectors from cache */
            .left-panel,
            .logo-panel {
              background: ${resourceCache.backgroundImage} !important;
              background-size: auto 100% !important;
            }
            
            /* Ensure the background doesn't repeat in mobile view */
            @media (max-width: 899.98px) {
              .left-panel,
              .logo-panel {
                background-size: cover !important;
                background-repeat: no-repeat !important;
                background-position: center center !important;
              }
            }
          `;
          
          return true;
        }
      }
      return false;
    });
  },

  /**
   * Updates the portal logo CSS in the DOM using the data from the API
   * @param {Object} cancelToken - Optional axios cancel token
   * @returns {Promise} - Promise that resolves when the portal logo is updated
   */
  updatePortalLogoNoBackground: (cancelToken) => {
    return DynamicResourcesUtils.retryWithAuth(async () => {
      const data = await MemberService.getPortalLogoNoBackground(cancelToken);
      
      if (data && typeof data === 'string') {
        // Extract image src if data contains an img tag
        let portalLogoUrl = data;
        
        // Check if data is an HTML img tag
        if (data.includes('<img') && data.includes('src=')) {
          const srcMatch = data.match(/src=["']([^"']*)["']/i);
          if (srcMatch && srcMatch[1]) {
            portalLogoUrl = `url("${srcMatch[1]}")`;
          }
        }
        
        // Store in cache for persistence
        resourceCache.portalLogo = portalLogoUrl;
        
        // Find existing style element or create a new one
        let styleEl = document.getElementById('dynamic-portal-logo-style');
        if (!styleEl) {
          styleEl = document.createElement('style');
          styleEl.id = 'dynamic-portal-logo-style';
          document.head.appendChild(styleEl);
        }
        
        // Apply the style to the specific selectors 
        styleEl.textContent = `
          /* Set CSS variable for the portal logo */
          :root {
            --dynamic-portal-logo: ${portalLogoUrl};
          }
          
          /* Direct override for the selectors */
          .left-panel .logo,
          .logo-panel .logo,
          .panel .logo-container.org .logo {
            background: var(--dynamic-portal-logo, url("../../images/portal-logo-no-background-fallback.png")) !important;
            background-size: contain !important;
            background-repeat: no-repeat !important;
            background-position: center center !important;
          }
        `;
        
        console.log('Portal logo updated successfully');
        return true;
      } else if (resourceCache.portalLogo) {
        // If we have a cached value but the API call returned nothing valid, use the cached value
        console.log('Using cached portal logo');
        
        // Find existing style element or create a new one
        let styleEl = document.getElementById('dynamic-portal-logo-style');
        if (!styleEl) {
          styleEl = document.createElement('style');
          styleEl.id = 'dynamic-portal-logo-style';
          document.head.appendChild(styleEl);
        }
        
        styleEl.textContent = `
          /* Set CSS variable for the portal logo from cache */
          :root {
            --dynamic-portal-logo: ${resourceCache.portalLogo};
          }
          
          /* Direct override for the selectors from cache */
          .left-panel .logo,
          .logo-panel .logo,
          .panel .logo-container.org .logo {
            background: var(--dynamic-portal-logo, url("../../images/portal-logo-no-background-fallback.png")) !important;
            background-size: contain !important;
            background-repeat: no-repeat !important;
            background-position: center center !important;
          }
        `;
        
        return true;
      } else {
        console.warn('Invalid portal logo data received');
        return false;
      }
    }).catch(error => {
      if (!axios.isCancel(error)) {
        console.error('Error updating portal logo:', error);
        
        // If we have a cached logo, use it as fallback on error
        if (resourceCache.portalLogo) {
          console.log('Using cached portal logo after error');
          
          // Find existing style element or create a new one
          let styleEl = document.getElementById('dynamic-portal-logo-style');
          if (!styleEl) {
            styleEl = document.createElement('style');
            styleEl.id = 'dynamic-portal-logo-style';
            document.head.appendChild(styleEl);
          }
          
          styleEl.textContent = `
            /* Set CSS variable for the portal logo from cache */
            :root {
              --dynamic-portal-logo: ${resourceCache.portalLogo};
            }
            
            /* Direct override for the selectors from cache */
            .left-panel .logo,
            .logo-panel .logo,
            .panel .logo-container.org .logo {
              background: var(--dynamic-portal-logo, url("../../images/portal-logo-no-background-fallback.png")) !important;
              background-size: contain !important;
              background-repeat: no-repeat !important;
              background-position: center center !important;
            }
          `;
          
          return true;
        }
      }
      return false;
    });
  },

  /**
   * Updates the logo icon in the DOM using the data from the API
   * @param {Object} cancelToken - Optional axios cancel token
   * @returns {Promise} - Promise that resolves when the logo icon is updated
   */
  updateLogoIcon: (cancelToken) => {
    return DynamicResourcesUtils.retryWithAuth(async () => {
      const data = await MemberService.getPortalLogoWithBackground(cancelToken);
      
      if (data && typeof data === 'string') {
        // Extract image src if data contains an img tag
        let iconUrl = data;
        
        // Check if data is an HTML img tag
        if (data.includes('<img') && data.includes('src=')) {
          const srcMatch = data.match(/src=["']([^"']*)["']/i);
          if (srcMatch && srcMatch[1]) {
            iconUrl = srcMatch[1];
          }
        }
        
        // Find existing logo icon link or create a new one
        let linkEl = document.querySelector('link[rel="logo-icon"]');
        if (!linkEl) {
          linkEl = document.createElement('link');
          linkEl.rel = 'logo-icon';
          document.head.appendChild(linkEl);
        }
        
        // Update the href attribute
        linkEl.href = iconUrl;
        
        console.log('Logo icon updated successfully');
        return true;
      } else {
        console.warn('Invalid logo icon data received');
        return false;
      }
    }).catch(error => {
      if (!axios.isCancel(error)) {
        console.error('Error updating logo icon:', error);
      }
      return false;
    });
  },
  
  /**
   * Updates the portal divider color in the DOM using the data from the API
   * @param {Object} cancelToken - Optional axios cancel token
   * @returns {Promise} - Promise that resolves when the divider color is updated
   */
  updatePortalDividerColor: (cancelToken) => {
    return DynamicResourcesUtils.retryWithAuth(async () => {
      const color = await MemberService.getPortalDividerColor(cancelToken);
      
      if (color && typeof color === 'string') {
        // Find existing style element or create a new one
        let styleEl = document.getElementById('dynamic-divider-color-style');
        if (!styleEl) {
          styleEl = document.createElement('style');
          styleEl.id = 'dynamic-divider-color-style';
          document.head.appendChild(styleEl);
        }
        
        // Apply the style to the specific selectors
        styleEl.textContent = `
          /* Set CSS variable for the divider color */
          :root {
            --dynamic-divider-color: ${color};
          }
          
          /* Direct override for the selectors */
          .left-panel hr {
            background-color: var(--dynamic-divider-color, #c3c3c3) !important;
          }
        `;
        
        console.log('Portal divider color updated successfully');
        return true;
      } else {
        console.warn('Invalid portal divider color received');
        return false;
      }
    }).catch(error => {
      if (!axios.isCancel(error)) {
        console.error('Error updating portal divider color:', error);
      }
      return false;
    });
  },
  
  /**
   * Updates the portal panel color in the DOM using the data from the API
   * @param {Object} cancelToken - Optional axios cancel token
   * @returns {Promise} - Promise that resolves when the panel color is updated
   */
  updatePortalPanelColor: (cancelToken) => {
    return DynamicResourcesUtils.retryWithAuth(async () => {
      const color = await MemberService.getPortalPanelColor(cancelToken);
      
      if (color && typeof color === 'string') {
        // Find existing style element or create a new one
        let styleEl = document.getElementById('dynamic-panel-color-style');
        if (!styleEl) {
          styleEl = document.createElement('style');
          styleEl.id = 'dynamic-panel-color-style';
          document.head.appendChild(styleEl);
        }
        
        // Apply the style to the specific selectors
        styleEl.textContent = `
          /* Set CSS variable for the panel color */
          :root {
            --dynamic-panel-color: ${color};
          }
          
          /* Direct override for the selectors */
          .panel {
            background-color: var(--dynamic-panel-color, #c3c3c3) !important;
          }
        `;
        
        console.log('Portal panel color updated successfully');
        return true;
      } else {
        console.warn('Invalid portal panel color received');
        return false;
      }
    }).catch(error => {
      if (!axios.isCancel(error)) {
        console.error('Error updating portal panel color:', error);
      }
      return false;
    });
  }
};

// Function to restore cached resources from local storage or session storage
// This helps ensure that resource settings persist across page refreshes
const restoreCachedResources = () => {
  // Currently we're relying on the in-memory cache which is reset on page reload
  // Resources will be reloaded from the API on each page load
  console.log('Resource cache ready for new values');
};

// Run the recovery on module load
if (typeof window !== 'undefined') {
  // Wait for DOM to be ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', restoreCachedResources);
  } else {
    restoreCachedResources();
  }
}

export default DynamicResourcesUtils;
